import { BreakpointObserver } from '@angular/cdk/layout';
import { DOCUMENT } from '@angular/common';
import { afterNextRender, Component, EnvironmentInjector, inject, Inject, OnInit, signal } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { ActivatedRoute, Router, RouterLink, RouterLinkActive } from '@angular/router';
import { StatusCodes } from 'http-status-codes';
import { MessageService } from 'services/message.service';
import { Utils } from 'utils/Utils';
import { NavigationService } from '@services/navigation/navigation.service';
import { Constants } from '@utils/Constants';
import { ComponentService } from '@services/component.service';
import { AuthorizationService } from '@services/authorization.service';
import { AuthenticationService } from '@services/authentication.service';
import { CentralServerService } from '@services/central-server.service';
import { TenantRepository } from '@repository/tenant/tenant.repository';
import { SpinnerService } from '@services/spinner.service';
import { finalize, map } from 'rxjs';
import { MatIcon } from '@angular/material/icon';
import { TranslateModule } from '@ngx-translate/core';
import { MatTooltipModule } from '@angular/material/tooltip';
import { UserAPIService } from '@api/user/user-api.service';
import { Action, Entity } from 'types/Authorization';
import { MenuData, VevRoute } from 'types/vev-route';
import { CertificationAdminRepository } from '@repository/certification-admin/certification-admin.repository';
import { ENV, Env } from '@providers/env.provider';
import { WindowService } from '../services/window.service';

@Component({
  selector: 'app-sidebar-cmp',
  templateUrl: 'sidebar.component.html',
  styleUrls: ['sidebar.component.scss'],
  imports: [RouterLink, MatIcon, RouterLinkActive, TranslateModule, MatTooltipModule],
})
export class SidebarComponent implements OnInit {
  private readonly authenticationService = inject(AuthenticationService);

  public mobileMenuVisible: any = 0;
  public menuItems!: any[];
  public readonly loggedUser = toSignal(this.authenticationService.getCurrentUserSubject(), {
    initialValue: this.authenticationService.getLoggedUser(),
  });
  public readonly loggedUserImage = signal(Constants.USER_NO_PICTURE);
  public isAdmin = false;
  public isSuperRole = false;
  public isSiteAdmin = false;
  public canEditProfile = false;
  public readonly logo = signal(Constants.NO_IMAGE);
  public sidebarMinimized = false;
  public hasDebts = false;
  public isSuperTenant = false;
  public canShowHelpSection = false;
  public isOpeningCertificationAdmin = false;
  public openedMenus: Set<string> = new Set();
  public readonly isSidebarCollapseFeatureActive: boolean;
  // Same breakpoint as the sidebar-mini styles in _sidebar-and-main-panel.scss
  private readonly isLargeScreen = toSignal(
    inject(BreakpointObserver)
      .observe('(min-width: 991px)')
      .pipe(map((state) => state.matches)),
    { initialValue: false },
  );

  // Labels are hidden only when the sidebar is minimized on large screens, so tooltips are only useful there
  public get isNavTooltipDisabled(): boolean {
    return !this.sidebarMinimized || !this.isLargeScreen();
  }

  public get canShowCertificationAdmin(): boolean {
    return this.isSuperTenant && this.authorizationService.isSuperAdmin() && !!this.env()?.certificationAdminApiUrl;
  }

  public ngOnInit(): void {
    this.openMenusForCurrentRoute();
  }

  private openMenusForCurrentRoute(): void {
    const currentUrl = this.router.url;
    this.menuItems?.forEach((menu) => {
      if (menu?.children) {
        menu.children.forEach((child: MenuData) => {
          if (currentUrl.startsWith('/' + child.path)) {
            this.openedMenus.add(menu.title);
          }
        });
      }
    });
  }

  public isMenuOpened(menu: MenuData): boolean {
    return this.openedMenus.has(menu.title);
  }

  public toggleMenu(menuTitle: string): void {
    if (this.openedMenus.has(menuTitle)) {
      this.openedMenus.delete(menuTitle);
      return;
    }
    this.openedMenus.add(menuTitle);
  }

  public constructor(
    private readonly router: Router,
    private readonly activatedRoute: ActivatedRoute,
    private readonly authorizationService: AuthorizationService,
    private readonly centralServerService: CentralServerService,
    private readonly messageService: MessageService,
    private readonly navigationService: NavigationService,
    private readonly componentService: ComponentService,
    private readonly spinnerService: SpinnerService,
    private readonly tenantRepository: TenantRepository,
    private readonly userService: UserAPIService,
    private readonly certificationAdminRepository: CertificationAdminRepository,
    private readonly windowService: WindowService,
    private readonly injector: EnvironmentInjector,
    @Inject(DOCUMENT) private readonly document: Document,
    @Inject(ENV) private readonly env: Env,
  ) {
    this.isSidebarCollapseFeatureActive = this.componentService.isFeatureActive('SIDEBAR_COLLAPSE');
    // Set Super Role
    this.isSuperRole = this.authorizationService.isSuperRole();
    // Get the routes
    if (this.activatedRoute?.routeConfig?.children) {
      const children: VevRoute[] = this.activatedRoute.routeConfig.children;

      this.menuItems = children
        .filter((route) => {
          // Show only in super tenant if both flags are set
          if (route.data?.hideFromNavbar && route.data?.displayInSuperTenant) {
            return this.windowService.isSuperTenant();
          }
          return !route.data?.hideFromNavbar;
        })
        .filter((route) => {
          if (route.data?.featureToggle && !this.componentService.isFeatureActive(route.data.featureToggle)) {
            return false;
          }
          const isAuthAllowed =
            route.data &&
            this.navigationService.isRouteAllowed(route.data?.auth) &&
            this.navigationService.isRoleAllowed(route.data?.authorizedRoles);
          if (this.isSuperRole) {
            return isAuthAllowed;
          }
          return (
            (isAuthAllowed && (route.data?.component ? this.componentService.isActive(route.data?.component) : true)) ||
            (this.authorizationService.isSuperAdmin() && route.data?.isAllowedInSuperTenantWhenInactive)
          );
        })
        .map((route) => {
          if (route?.data?.menu?.children) {
            // Filter children based on auth
            const filteredChildren = route.data.menu.children.filter((child: any) => {
              const isChildAuthAllowed =
                this.navigationService.isRouteAllowed(child.auth) &&
                this.navigationService.isRoleAllowed(child.authorizedRoles);
              return isChildAuthAllowed;
            });
            if (!filteredChildren.length) {
              return null;
            }
            return {
              ...route.data.menu,
              children: filteredChildren,
            };
          }
          return route?.data ? route.data.menu : null;
        })
        .filter((menu) => menu);
    }
    // Set admin
    this.isAdmin = this.authorizationService.isAdmin() || this.authorizationService.isSuperAdmin();
    this.isSiteAdmin = this.authorizationService.getSitesAdmin().length > 0;
    // Show help section only if the user has read access to the sidebar
    this.canShowHelpSection = this.navigationService.isRouteAllowed({
      entity: Entity.HELP,
      action: Action.READ,
    });
    if (authorizationService.canUpdateUser()) {
      this.canEditProfile = true;
    }

    this.isSuperTenant = this.windowService.isSuperTenant();
    // Read user
    afterNextRender(() => {
      this.loadUser();
      // Read tenant
      this.loadTenant();
    });
  }

  public toggleSidebar(): void {
    const body = this.document.getElementsByTagName('body')[0];
    if (this.sidebarMinimized === true) {
      body.classList.remove('sidebar-mini');
      this.sidebarMinimized = false;
    } else {
      body.classList.add('sidebar-mini');
      this.sidebarMinimized = true;
    }
  }

  public logout(): void {
    this.spinnerService.show();
    this.authenticationService
      .logout()
      .pipe(
        finalize(() => {
          this.spinnerService.hide();
        }),
      )
      .subscribe();
  }

  public openCertificationAdmin(): void {
    if (this.isOpeningCertificationAdmin) return;

    const adminWindow = this.document.defaultView?.open('about:blank', '_blank');
    if (!adminWindow) {
      this.messageService.showErrorMessage('general.popup_blocked');
      return;
    }
    adminWindow.opener = null;
    this.isOpeningCertificationAdmin = true;
    let navigated = false;

    this.certificationAdminRepository
      .createSession()
      .pipe(
        finalize(() => {
          this.isOpeningCertificationAdmin = false;
          // Covers errors and silent completions (e.g. a request swallowed by an interceptor)
          if (!navigated) adminWindow.close();
        }),
      )
      .subscribe({
        next: ({ adminUrl }) => {
          navigated = true;
          adminWindow.location.replace(adminUrl);
        },
        error: () => this.messageService.showErrorMessage('general.unexpected_error_backend'),
      });
  }

  private loadTenant(): void {
    // Get Tenant logo
    this.tenantRepository.getTenantLogo().subscribe({
      next: (tenantLogo) => {
        this.logo.set(tenantLogo);
      },
    });
  }
  private loadUser(): void {
    // Get the user's image
    const loggedUser = this.loggedUser();
    if (loggedUser?.id) {
      this.userService.getUsersDebts(loggedUser.id).subscribe({
        next: (hasUserDebts) => {
          this.hasDebts = hasUserDebts;
        },
      });

      this.centralServerService.getUserImage(loggedUser.id).subscribe({
        next: (userImage) => {
          this.loggedUserImage.set(userImage ?? Constants.USER_NO_PICTURE);
        },
        error: (error) => {
          if (error.status === StatusCodes.NOT_FOUND) {
            this.loggedUserImage.set(Constants.USER_NO_PICTURE);
          } else {
            Utils.handleHttpError(
              error,
              this.router,
              this.messageService,
              this.windowService,
              'general.unexpected_error_backend',
            );
          }
        },
      });
    }
  }
}
